import _ from "lodash"
import moment from "moment"
import {translateName} from "../../../../utils/i18n"
import {
    setFieldVisibility,
    setFieldEdition,
    setFormButtons,
    changeFieldRequired, setFieldStartDate, changeFieldProperty
} from "../../../../apps/KpModule/actions"
import {GET_OBJECT_SUCCESS} from "../../../../apps/KpModule/actions/api"
import Errors from "../../../utils/Errors"

const saveButton                  = { type: 'save', tKey: 'save', bsStyle: 'success' }
const requestImplementationButton = { type: 'action', action: 'requestImplementation', tKey: 'requestImplementation', bsStyle: 'primary' }
const submitResolutionButton      = { type: 'action', action: 'submitResolution', tKey: 'submitResolution', bsStyle: 'primary' }
const closeControlButton          = { type: 'action', action: 'closeControl', tKey: 'closeControl', bsStyle: 'danger' }
const notApplicableButton         = { type: 'action', action: 'notApplicable', tKey: 'notApplicable', bsStyle: 'warning' }
const returnButton                = { type: 'return', tKey: 'return', bsStyle: 'default' }

export const entity = {
    name: "AuditPreparationControl",
    facets: ['conversations', 'files'],
    fields: [
        {path: "auditPreparation", type: "AuditPreparation"},
        {path: "auditControl", type: "AuditControl"},
        {path: "status", type: "AuditControlInstanceStatus"},
        {path: "conformityRate", type: "AuditConformityRate", nullable: true},
        {path: "risks", type: "AuditRisk", link: "MTM", nullable: true},
        {path: "initialAuditorComment", nullable: true},
        {path: "auditorRecommendation", nullable: true},
        {path: "implementationDate", type: "date", nullable: true},
        {path: "storeComment", nullable: true},
        {path: "updatedAt", type: "date", dateFormat: 'YYYY-MM-DD HH:mm', nullable: true},
        {path: "updatedBy", type: "User", nullable: true},
        {
            path: "auditNumber",
            fieldPath: ["auditPreparation.auditNumber"],
            f: function() { return _.get(this, "auditPreparation.auditNumber") }
        },
        {
            path: "store",
            fieldPath: ["auditPreparation.store.id", "auditPreparation.store.fullName", "auditPreparation.store.code"],
            f: function() { return _.get(this, "auditPreparation.store.fullName") }
        },
        {
            path: "storeContacts",
            fieldPath: ["auditPreparation.storeContacts.userName"],
            f: function() {
                return (_.get(this, "auditPreparation.storeContacts") || []).map(sc => sc.userName).join(', ')
            }
        },
        {
            path: "hierarchicalContacts",
            fieldPath: ["auditPreparation.hierarchicalContacts.userName"],
            f: function() {
                return (_.get(this, "auditPreparation.hierarchicalContacts") || []).map(hc => hc.userName).join(', ')
            }
        },
        {
            path: "controlDate",
            fieldPath: ["auditPreparation.controlDate"],
            f: function() { return _.get(this, "auditPreparation.controlDate") }
        },
        {
            path: "auditTheme",
            fieldPath: ["auditControl.auditTheme.name"],
            $f: function(control, context, callback) {
                const raw = _.get(control, "auditControl.auditTheme.name") || ''
                return callback(null, translateName(raw, context.language))
            }
        },
        {
            path: "auditControlType",
            fieldPath: ["auditControl.auditControlType.translatedFullName"],
            $f: function(control, context, callback) {
                const raw = _.get(control, "auditControl.auditControlType.translatedFullName") || ''
                return callback(null, translateName(raw, context.language))
            }
        },
        {
            path: "code",
            fieldPath: ["auditControl.code"],
            f: function() { return _.get(this, "auditControl.code") }
        },
        {
            path: "controlsToPerform",
            fieldPath: ["auditControl.name"],
            $f: function(control, context, callback) {
                const raw = _.get(control, "auditControl.name") || ''
                return callback(null, translateName(raw, context.language))
            }
        },
        {
            path: 'controlsToPerformAndSuggestions',
            fieldPath: ["auditControl.description"],
            $f: function(control, context, callback) {
                return callback(null, _.get(control, "auditControl.description") || '')
            }
        },
        {
            path: "weight",
            fieldPath: ["auditControl.weight.value"],
            f: function() { return _.get(this, "auditControl.weight.value") }
        },
        {
            path: "isContact",
            fieldPath: [
                "auditPreparation.storeContacts.user.id",
                "auditPreparation.hierarchicalContacts.user.id"
            ],
            $f: function(control, context, callback) {
                const scUserIds = (_.get(control, "auditPreparation.storeContacts") || []).map(sc => sc.user.id)
                const hcUserIds = (_.get(control, "auditPreparation.hierarchicalContacts") || []).map(hc => hc.user.id)
                return callback(null, scUserIds.includes(context.user.id) || hcUserIds.includes(context.user.id))
            }
        },
        {
            path: "isAuditor",
            $f: async function(control, context, callback) {
                try {
                    const prepId = _.get(control, "auditPreparation.id")
                    if (!prepId) return callback(null, false)

                    const prep = await global.app.S.AuditPreparation.get(prepId, {
                        group: context.group,
                        fieldPath: ["auditors.user.id"]
                    })

                    const auditorUserIds = (_.get(prep, "auditors") || [])
                        .map(hab => _.get(hab, "user.id"))
                        .filter(Boolean)

                    return callback(null, auditorUserIds.includes(context.user.id))
                } catch(e) {
                    return callback(null, false)
                }
            }
        },
        {
            path: "niveau",
            fieldPath: ["conformityRate.id"],
            nullable: true,
            f: function() {
                const colorMap = {
                    '0': '#CC2200',
                    '25': '#FF0000',
                    '50': '#FF8C00',
                    '75': '#FFD700',
                    '100': '#249b24'
                }
                const color = colorMap[_.get(this, "conformityRate.id")]
                if (!color) return null
                return { id: _.get(this, "conformityRate.id"), label: '●', style: {background: 'transparent', color: color } }
            }
        },
        {
            path: "orangeStyledRow",
            fieldPath: ["status.id", "implementationDate"],
            $f: function(control, context, callback) {
                const today = moment().startOf('day').toDate()
                const isOverdue =
                    _.get(control, "status.id") === 'inFollowUp' &&
                    control.implementationDate && new Date(control.implementationDate) < today
                return callback(null, isOverdue)
            }
        },
        {
            path: "buttons",
            fieldPath: [
                "status.id",
                "auditPreparation.auditors.user.id",
                "auditPreparation.storeContacts.user.id",
                "auditPreparation.hierarchicalContacts.user.id",
                "isAuditor"
            ],
            $f: async function(control, context, callback) {
                try {
                    const statusId = _.get(control, "status.id")
                    const userId = context.user.id

                    const prep = _.get(control, "auditPreparation")
                    const scUserIds = (_.get(prep, "storeContacts") || []).map(sc => sc.user.id)
                    const hcUserIds = (_.get(prep, "hierarchicalContacts") || []).map(hc => hc.user.id)

                    let isAuditor = _.get(control, "isAuditor")
                    let isStoreContact = scUserIds.includes(userId)
                    let isHierarchicalContact = hcUserIds.includes(userId)

                    const isContact = isStoreContact || isHierarchicalContact
                    const buttons = []

                    // Enregistrer: visible if Auditeur OR (inFollowUp AND contact)
                    if (!['closed', 'notApplicable', 'notControlled'].includes(statusId) && (isAuditor || isContact)) {
                        buttons.push(saveButton)
                    }

                    // Demander la mise en œuvre: (inPreparation OR inRealization OR pendingDecision) AND Auditeur
                    if (['inPreparation', 'inRealization', 'pendingDecision'].includes(statusId) && isAuditor) {
                        buttons.push(requestImplementationButton)
                    }

                    // Transmettre la preuve: inFollowUp AND contact
                    if (statusId === 'inFollowUp' && isContact) {
                        buttons.push(submitResolutionButton)
                    }

                    // Clôturer le contrôle: (inPreparation OR inRealization OR inFollowUp OR pendingDecision) AND Auditeur
                    if (['inPreparation', 'inRealization', 'inFollowUp', 'pendingDecision'].includes(statusId) && isAuditor) {
                        buttons.push(closeControlButton)
                    }

                    // Non applicable: (inPreparation OR inRealization OR pendingDecision) AND Auditeur
                    if (['inPreparation', 'inRealization', 'pendingDecision'].includes(statusId) && isAuditor) {
                        buttons.push(notApplicableButton)
                    }

                    buttons.push(returnButton)
                    return callback(null, buttons)
                } catch(e) {
                    return callback(e)
                }
            }
        }
    ],
    filters: [
        {
            name: "byCurrentUser",
            isDefault: false,
            async: true,
            query: async function(context, callback) {
                try {
                    const userId = context.user.id
                    const preps = await global.app.S.AuditPreparation.find({
                        group: context.group,
                        query: {},
                        fieldPath: ['id', 'auditors.user.id', 'storeContacts.user.id', 'hierarchicalContacts.user.id']
                    })
                    const auditorPrepIds = []
                    const contactPrepIds = []
                    preps.forEach(p => {
                        const isAuditor = (_.get(p, 'auditors') || []).some(a => _.get(a, 'user.id') === userId)
                        const isSC = (_.get(p, 'storeContacts') || []).some(a => _.get(a, 'user.id') === userId)
                        const isHC = (_.get(p, 'hierarchicalContacts') || []).some(a => _.get(a, 'user.id') === userId)
                        if (isAuditor) auditorPrepIds.push(new global.ObjectID(p.id))
                        else if (isSC || isHC) contactPrepIds.push(new global.ObjectID(p.id))
                    })
                    const conditions = []
                    if (auditorPrepIds.length) conditions.push({auditPreparation: {$in: auditorPrepIds}})
                    if (contactPrepIds.length) conditions.push({auditPreparation: {$in: contactPrepIds}, status: 'inFollowUp'})
                    if (!conditions.length) return callback(null, {_id: null})
                    return callback(null, conditions.length === 1 ? conditions[0] : {$or: conditions})
                } catch(e) {
                    return callback(null, {_id: null})
                }
            }
        },
        {
            name: "byAuditPreparation",
            path: 'auditPreparations',
            tKey: 'auditNumber',
            object: 'AuditPreparation',
            display: "auditNumber",
            client: true,
            default: [],
            isDefault: false,
            type: 'tags',
            filters: ["byCurrentUser", "excludeDraft"],
            width: 4,
            query: function(context) {
                const auditPreparations = _.get(context, 'data.auditPreparations') || []
                if (!auditPreparations.length) return {}
                return {auditPreparation: {$in: auditPreparations.map(a => new global.ObjectID(a.id))}}
            }
        },
        {
            name: "byStore",
            path: 'stores',
            tKey: 'store',
            object: 'Store',
            display: "fullName",
            client: true,
            default: [],
            isDefault: false,
            type: 'tags',
            filters: ["byCurrentUserAuditPreparations"],
            async: true,
            width: 4,
            query: async function(context, callback) {
                const stores = _.get(context, 'data.stores') || []
                if (!stores.length) return callback(null, {})
                const storeIds = stores.map(s => new global.ObjectID(s.id))
                const preps = await global.app.S.AuditPreparation.find({
                    group: context.group,
                    query: {store: {$in: storeIds}},
                    fieldPath: ['id']
                })
                if (!preps.length) return callback(null, {_id: null})

                let prepIds = preps.map(p => p.id)

                // Both byStore and byAuditPreparation resolve to the same query key, so the
                // last filter in the list overwrites the other. We compute the intersection
                // here so the combined result is correct when both filters are active.
                const selectedPreps = _.get(context, 'data.auditPreparations') || []
                if (selectedPreps.length) {
                    const selectedIds = new Set(selectedPreps.map(a => a.id))
                    prepIds = prepIds.filter(id => selectedIds.has(id))
                    if (!prepIds.length) return callback(null, {_id: null})
                }

                return callback(null, {auditPreparation: {$in: prepIds.map(id => new global.ObjectID(id))}})
            }
        },
        {
            name: "byStatuses",
            path: 'statuses',
            tKey: 'controlStatus',
            object: 'AuditControlInstanceStatus',
            display: "name",
            translate: true,
            client: true,
            default: [],
            isDefault: false,
            type: 'tags',
            sortList: false,
            width: 4,
            query: function(context) {
                const statuses = _.get(context, 'data.statuses') || []
                if (!statuses.length) return {}
                return {status: {$in: statuses.map(s => s.id)}}
            }
        }
    ],
    validateSave: async function(object, oldObject, context, callback) {
        if (!['save', 'notApplicable'].includes(context.action) && !_.get(object, 'conformityRate.id')) {
            return callback(new Errors.ValidationError(context.tc('conformityRateRequired')))
        }
        return callback(null, object, oldObject)
    },
    beforeSave: async function(object, oldObject, context, callback) {
        try {
            object.updatedAt = new Date()
            object.updatedBy = _.pick(context.user, ['id', 'name'])

            const action = context.action

            // ── Enregistrer ───────────────────────────────────────────────
            if (!action || action === 'save') {
                return callback(null, object, oldObject)
            }

            // ── Demander la mise en œuvre des recommandations ─────────────
            if (action === 'requestImplementation') {
                object.status = {id: 'inFollowUp'}

                const statusLabel = 'inFollowUp'
                const recommendation = object.auditorRecommendation || ''

                const entries = []
                if (recommendation) entries.push({
                    sender: _.pick(context.user, ['id', 'name']),
                    timestamp: new Date().toISOString(),
                    seenBy: [context.user.id],
                    files: [],
                    from: 'user',
                    text: recommendation,
                })

                entries.push({
                    sender: _.pick(context.user, ['id', 'name']),
                    timestamp: new Date().toISOString(),
                    seenBy: [context.user.id],
                    files: [],
                    from: 'system',
                    text: statusLabel,
                })

                object.conversations = [...(object.conversations || []), ...entries]
                return callback(null, object, oldObject)
            }

            // ── Transmettre la preuve de résolution ───────────────────────
            if (action === 'submitResolution') {
                object.status = {id: 'pendingDecision'}

                const statusLabel = 'pendingDecision'
                const comment = object.storeComment || ''

                const commentEntries = []
                if (comment) commentEntries.push({
                    sender: _.pick(context.user, ['id', 'name']),
                    timestamp: new Date().toISOString(),
                    seenBy: [context.user.id],
                    files: [],
                    from: 'user',
                    text: comment,
                })

                commentEntries.push({
                    sender: _.pick(context.user, ['id', 'name']),
                    timestamp: new Date().toISOString(),
                    seenBy: [context.user.id],
                    files: [],
                    from: 'system',
                    text: statusLabel,
                })
                object.conversations = [...(object.conversations || []), ...commentEntries]
                return callback(null, object, oldObject)
            }

            // ── Clôturer le contrôle ──────────────────────────────────────
            if (action === 'closeControl') {
                object.status = {id: 'closed'}

                const recommendation = object.auditorRecommendation || ''
                const entries = []

                if (recommendation) entries.push({
                    sender: _.pick(context.user, ['id', 'name']),
                    timestamp: new Date().toISOString(),
                    seenBy: [context.user.id],
                    files: [],
                    from: 'user',
                    text: recommendation,
                })

                entries.push({
                    sender: _.pick(context.user, ['id', 'name']),
                    timestamp: new Date().toISOString(),
                    seenBy: [context.user.id],
                    files: [],
                    from: 'system',
                    text: 'closed',
                })

                object.conversations = [...(object.conversations || []), ...entries]

                return callback(null, object, oldObject)
            }

            // ── Non applicable ────────────────────────────────────────────
            if (action === 'notApplicable') {
                object.status = {id: 'notApplicable'}
                /*object.conformityRate = {id: '100'}
                object.implementationDate = null
                object.risks = []
                object.auditorRecommendation = ''*/

                object.conversations = [
                    ...(object.conversations || []),
                    {
                        sender: _.pick(context.user, ['id', 'name']),
                        timestamp: new Date().toISOString(),
                        seenBy: [context.user.id],
                        files: [],
                        from: 'system',
                        text: 'notApplicable',
                    }
                ]
                return callback(null, object, oldObject)
            }

            return callback(null, object, oldObject)
        } catch(e) {
            return callback(e)
        }
    }
}

export const module_ = {
    object: "AuditPreparationControl",
    tKey: "mTitle_auditPreparationControl",
    category: {
        path: "audit",
        icon: "clipboardCheck"
    },
    newable: false,
    defaultSortBy: "auditNumber",
    defaultSortDirection: "ASC",
    filters: [
        'byCurrentUser',
        'byAuditPreparation',
        'byStore',
        //'byStatuses'
    ],
    viewMap: {
        dt: [
            {path: "auditNumber", tKey: "auditNumber"},
            {path: "auditPreparation.store.fullName", tKey: "store", initiallyNotVisible: true},
            {path: "auditPreparation.controlDate", tKey: "scheduledAuditDate", initiallyNotVisible: true},
            {path: "storeContacts", tKey: "storeContacts", initiallyNotVisible: true},
            {path: "hierarchicalContacts", tKey: "hierarchicalContacts", initiallyNotVisible: true},
            {path: "auditControl.auditTheme.name", translateName: true, tKey: "auditTheme"},
            {path: "auditControl.auditControlType.translatedFullName", tKey: "controlType", initiallyNotVisible: true},
            {path: "auditControl.code", tKey: "code", initiallyNotVisible: true},
            {path: "controlsToPerform", tKey: "controlSubject", translateName: true},
            {path: "status", display: "name", tKey: "status"},
            {path: "niveau", tKey: "niveau", type: 'status', display: 'label'},
            {path: "updatedAt", tKey: "MAJ le", initiallyNotVisible: true},
            {path: "updatedBy", display: "name", tKey: "MAJ par", initiallyNotVisible: true},
            {path: 'orangeStyledRow', hidden: true}
        ],
        form: [
            {path: "auditNumber", tKey: "auditNumber", disabled: true},
            {path: "storeContacts", tKey: "storeContacts", disabled: true, widthClassName: "col-xs-12 col-sm-6"},
            {path: "hierarchicalContacts", tKey: "hierarchicalContacts", disabled: true, widthClassName: "col-xs-12 col-sm-6"},
            {path: "auditTheme", tKey: "auditTheme", disabled: true, translateName: true},
            {path: "auditControlType", display: 'translatedFullName', tKey: "controlType", disabled: true},
            {path: "code", tKey: "code", disabled: true},
            {path: "controlsToPerform", tKey: "controlSubject", disabled: true, type: 'textarea'},
            {path: "controlsToPerformAndSuggestions", disabled: true, type: 'richTextEditor'},
            {path: "weight", tKey: "weight", disabled: true},
            {path: "initialAuditorComment", tKey: "auditorComment", type: "richTextEditor"},
            {
                path: "conformityRate", tKey: "conformityRate", display: "label", sortList: false,
                subscriptions: {
                    onChange: (newValue, oldValue, {module, store}) => {
                        const state = store.getState()
                        const originalButtons = _.get(state, 'edit.object.data.buttons', [])



                        const statusField = module.viewMap.form.fields.find(f => f.path === 'status')
                        const statusId = _.get(statusField.getValue(), "id")
                        const conformityRateId = _.get(newValue, "id")
                        const showRecommendation = ['0', '25', '50', '75'].includes(conformityRateId) && !['notApplicable', 'notControlled'].includes(statusId)
                        const isAuditor = _.get(state, "edit.object.data.isAuditor", false)
                        store.dispatch(setFieldVisibility("e_auditorRecommendation", showRecommendation))
                        store.dispatch(setFieldVisibility("e_risks", showRecommendation && isAuditor))
                        store.dispatch(setFieldVisibility("e_implementationDate", showRecommendation))

                        store.dispatch(changeFieldRequired("e_risks", !!conformityRateId && conformityRateId !== '100'))
                        store.dispatch(changeFieldRequired("e_auditorRecommendation", !!conformityRateId && conformityRateId !== '100'))
                        store.dispatch(changeFieldRequired("e_implementationDate", !!conformityRateId && conformityRateId !== '100'))

                        const showStoreComment = ['0', '25', '50', '75'].includes(conformityRateId) && ['inFollowUp', 'pendingDecision', 'closed'].includes(statusId)
                        store.dispatch(setFieldVisibility("e_storeComment", showStoreComment))

                        if ([null, undefined, '100'].includes(conformityRateId)) {
                            const fields = module.viewMap.form.fields
                            fields.find(f => f.path === 'implementationDate').setValue(null)
                            fields.find(f => f.path === 'risks').setValue([])
                            fields.find(f => f.path === 'auditorRecommendation').setValue('')
                        }

                        if (!!conformityRateId) {
                            let filteredButtons = originalButtons.filter(b => b.action !== notApplicableButton.action)
                            if (conformityRateId === '100') filteredButtons = filteredButtons.filter(b => b.action !== requestImplementationButton.action && b.action !== submitResolutionButton.action)
                            store.dispatch(setFormButtons(filteredButtons))
                        } else {
                            const filteredButtons = originalButtons.filter(b => b.action !== requestImplementationButton.action && b.action !== closeControlButton.action)
                            store.dispatch(setFormButtons(filteredButtons))
                        }
                    }
                }
            },
            {path: "risks", display: 'translatedFullName', tKey: "risks", filters: ["activeRisks"], translateName: true},
            {path: "auditorRecommendation", tKey: "auditorRecommendation", type: "richTextEditor"},
            {path: "implementationDate", tKey: "plannedImplementationDate"},
            {path: "storeComment", tKey: "storeComment", type: "richTextEditor"},
            {path: "files", tKey: "files", type: 'simpleFiles'},
            {path: "conversations", tKey: "followUp"},
            {path: "status", hidden: true},
            {path: "isContact", hidden: true},
            {path: "isAuditor", hidden: true},
            {path: "buttons", hidden: true},

            {path: 'auditPreparation', hidden: true},
            {path: 'auditControl', hidden: true},
        ]
    },
    actionSubscriptions: [
        {
            actionType: GET_OBJECT_SUCCESS,
            subscription: ({store, action}) => {
                const state = store.getState()
                const isAuditor = _.get(state, "edit.object.data.isAuditor", false)

                store.dispatch(setFieldVisibility("e_storeContacts", isAuditor))
                store.dispatch(setFieldVisibility("e_hierarchicalContacts", isAuditor))
                store.dispatch(setFieldVisibility("e_auditTheme", isAuditor))
                store.dispatch(setFieldVisibility("e_auditControlType", isAuditor))
                store.dispatch(setFieldVisibility("e_weight", isAuditor))
                store.dispatch(setFieldVisibility("e_initialAuditorComment", isAuditor))
                store.dispatch(setFieldVisibility("e_conversations", isAuditor))
                store.dispatch(setFieldVisibility("e_controlsToPerformAndSuggestions", isAuditor))

                const statusId = _.get(state, "edit.object.data.status.id")
                store.dispatch(setFieldVisibility("e_conformityRate", !['notControlled', 'notApplicable'].includes(statusId) && isAuditor))
                store.dispatch(setFieldEdition("e_risks", ['inPreparation', 'inRealization'].includes(statusId)))
                store.dispatch(setFieldEdition("e_conformityRate", ['inPreparation', 'inRealization'].includes(statusId)))

                const isContact = _.get(state, "edit.object.data.isContact", false)
                store.dispatch(setFieldEdition("e_auditorRecommendation", !isContact && ['inPreparation', 'inRealization', 'pendingDecision'].includes(statusId)))
                store.dispatch(setFieldEdition("e_implementationDate", !isContact && ['inPreparation', 'inRealization', 'pendingDecision'].includes(statusId)))
                store.dispatch(setFieldEdition("e_storeComment", isContact && statusId === 'inFollowUp'))

                const controlDate = _.get(state, "edit.object.data.auditPreparation.controlDate")
                const tomorrow = moment().add(1, 'day').format("YYYY-MM-DD")
                const startDate = controlDate
                    ? moment.max(moment(controlDate), moment(tomorrow)).format("YYYY-MM-DD")
                    : tomorrow
                store.dispatch(setFieldStartDate("e_implementationDate", startDate))
                store.dispatch(changeFieldProperty("e_implementationDate", "defaultCalendarValue", startDate))
            }
        }
    ]
}